import styled from "@emotion/styled";
import { KmsTypePublishExternalCategories, CategoryType } from "@kaltura/mediaspace-shared-types";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Typography } from "@kaltura/ds-react-components";
import { enrichCategoriesData, EnrichmentType } from "../../utils";
import { CategoriesList, CategoriesListVariant } from "../categories-list/CategoriesList";

interface ExternalAppPublishedCategoriesProps {
    variant: CategoriesListVariant;
    externalCategories: KmsTypePublishExternalCategories;
}

const StyledAppTitle = styled(
    Typography,
    {
        shouldForwardProp: (propName) => propName !== "dense",
    }
)<{ dense: boolean }>(({ theme, dense }) => ({
    color: theme.kaltura.palette.tone2,
    display: "block",
    marginTop: theme.spacing(dense ? 1 : 2),
}));

const StyledExternalCategoriesContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(1),
}));

/**
 * Publish menu - External published categories for one external app
 */
export function ExternalAppPublishedCategories({ variant, externalCategories }: ExternalAppPublishedCategoriesProps) {
    // get the external categories
    const { categories = {}, applicationName = "" } = externalCategories;
    const { published = [], pending = [], rejected = [] } = categories;

    const publishedExternalCategories = enrichCategoriesData(
        published,
        CategoryType.externalCategory,
        EnrichmentType.label
    );
    const pendingExternalCategories = enrichCategoriesData(
        pending,
        CategoryType.externalCategory,
        EnrichmentType.label
    );
    const rejectedExternalCategories = enrichCategoriesData(
        rejected,
        CategoryType.externalCategory,
        EnrichmentType.label
    );

    const hasExternalCategories =
        publishedExternalCategories.length + pendingExternalCategories.length + rejectedExternalCategories.length > 0;

    return (
        hasExternalCategories && (
            <>
                <StyledAppTitle variant="body1Highlight" dense={variant === "dense"}>
                    {translate("Published in %1:", [applicationName])}
                </StyledAppTitle>

                <CategoriesList variant={variant} categories={publishedExternalCategories} />

                <CategoriesList
                    variant={variant}
                    categories={pendingExternalCategories}
                    title={{ label: translate("Pending in"), color: "warning.main" }}
                />

                <CategoriesList
                    variant={variant}
                    categories={rejectedExternalCategories}
                    title={{ label: translate("Rejected in"), color: "error.main" }}
                />
            </>
        )
    );
}

export interface ExternalPublishedCategoriesProps {
    variant: CategoriesListVariant;
    externalCategories: KmsTypePublishExternalCategories[];
}

/**
 * Publish menu - External published categories for several external apps
 */
export function ExternalPublishedCategories({ variant, externalCategories = [] }: ExternalPublishedCategoriesProps) {
    return (
        <StyledExternalCategoriesContainer>
            {externalCategories.map((externalAppCategories) => (
                <ExternalAppPublishedCategories
                    key={externalAppCategories.applicationName}
                    variant={variant}
                    externalCategories={externalAppCategories}
                />
            ))}
        </StyledExternalCategoriesContainer>
    );
}
